/*
 * Copyright (c) 2022, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import PropTypes from 'prop-types'
import {useParams} from 'react-router-dom'
import {useIntl} from 'react-intl'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {setInitialScriptState} from '../../contexts/script-context'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useBrand} from '../../contexts/brand-context'
import {getSiteEnvVarValue} from '../../utils/site-config'
import {generateAlternateUrls} from '../../utils/utils'
import {renderLayoutsSwitch} from '../../components/optimizely/utils/contentRenderer'
import {HTTPError} from '@salesforce/pwa-kit-react-sdk/ssr/universal/errors'

// Components
import {
    Box,
    Flex,
    Grid,
    Select,
    Button,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {HideOnDesktop, HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive'
import Seo from '../../components/seo'
import Optimizely from '../../components/optimizely'
import {GDIDynamicContentProvider} from '../../components/optimizely/gdi/utils/gdi-dynamic-content-context'
import {useGlobalLowResultsContent} from '../../contexts/global-elements-context'

// Partials
import AbovePageHeader from './partials/above-page-header'
import PageHeader from './partials/page-header'
import PageFilters from './partials/page-filters'
import SelectedRefinements from './partials/selected-refinements'
import EmptySearchResults from './partials/empty-results'
import EmptySearchResultsTitle from './partials/empty-results-title'
import LowResultsCarousel from './partials/low-results-carousel'
import ProductGrid from './partials/product-grid'

// Constants
import {DEFAULT_LIMIT_VALUES} from '@salesforce/retail-react-app/app/constants'

// Error page
import PageNotFound from '../page-not-found'

// Page hook - all state, queries, effects and action handlers
import {useProductList} from './use-product-list'

/*
 * This is a simple product listing page. It displays a paginated list
 * of product hit objects. Allowing for sorting and filtering based on the
 * allowable filters and sort refinements.
 */
const ProductList = (props) => {
    // eslint-disable-next-line react/prop-types, @typescript-eslint/no-unused-vars
    const {isLoading: _unusedIsLoading, staticContext, ...rest} = props
    const {formatMessage} = useIntl()
    const pageFiltersStyles = useMultiStyleConfig('PageFilters')
    const {app: appConfig} = getConfig()
    const {site} = useMultiSite()
    const siteId = site?.id
    const {brandId} = useBrand()
    // PLP category URLs carry the brand as a path segment (/g/ -> garage, /d/ -> dynamite),
    // which is identical on SSR and client. Prefer it for SEO over hostname-based useBrand,
    // which defaults to garage on SSR when the host has no brand slug (e.g. plain localhost).
    const {brandId: routeBrandSegment} = useParams()
    const seoBrandId =
        routeBrandSegment === 'g'
            ? 'garage'
            : routeBrandSegment === 'd'
              ? 'dynamite'
              : brandId
    const {data: lowResultsContent} = useGlobalLowResultsContent()
    if (getSiteEnvVarValue(appConfig, 'IS_NOIBU_DISABLED_PLP', siteId) === 'true') {
        setInitialScriptState('noibu', false)
    }

    const {
        // State
        sortOpen, setSortOpen, filtersLoading, gridView, setGridViewPersisted,
        skeletonPlaceholders, allProducts, isCategoryChanging, page,
        minPageLoaded, maxPageLoaded, isLoadingNewPage, showLoadPrevious,
        // Search
        isLoading, isFetched, isRefetching, productSearchResult,
        searchQuery, isSearch, searchParams,
        // Category / CMS
        category,
        clpPage, isClpMode, isClpLoading,
        searchNoResultsPage, isSearchNoResultsLoading, hasSearchNoResultsPage,
        effectiveCategoryId, hasSEOUrlParams, serverSeoNotFound, clientSeoNotFound,
        // Grid
        isAlternativeGridView, gridColumns, currentBreakpoint, isDesktopBreakpoint,
        desktopGroupSize, gridInterruptorPositions,
        // CMS sections (PLP)
        aboveProductListBlocks, belowProductListBlocks,
        // Derived
        error, showNoResults, isSmartSearch, hasSelectedFilters, total, sortingOptions,
        sortUrls, limitUrls, basePath,
        pageTitle, limit, getLimitForPage, loadedLimitTotal, seoMeta,
        // Handlers
        toggleFilter, resetFilters, goToPage, loadPrevious, handleProductClick,
        addItemToWishlist, removeItemFromWishlist,
        // Enrichment / tracking
        enrichProduct, observeProduct, productRefs,
        prependScrollAnchor,
        // Wishlist
        wishlist,
        // Navigation
        history, location, onOpen
    } = useProductList()

    /**************** Error Handling ****************/
    const shouldRenderSeoNotFound =
        hasSEOUrlParams && !effectiveCategoryId && (serverSeoNotFound || clientSeoNotFound)

    if (shouldRenderSeoNotFound) return <PageNotFound />

    const hideFiltersAndSort = category?.c_hideFiltersAndSort === true || isSmartSearch === true

    const errorStatus = error?.response?.status
    switch (errorStatus) {
        case undefined:
            break
        case 404:
            return <PageNotFound />
        default:
            throw new HTTPError(errorStatus, `HTTP Error ${errorStatus} occurred.`)
    }

    // CLP mode: Optimizely has authored a page keyed by this category id —
    // render it as a full CMS replacement (no grid, filters, or PLP chrome).
    if (isClpMode) {
        return (
            <Box data-testid="sf-product-list-page" data-clp="true" {...rest}>
                <Optimizely content={clpPage} isLoading={isClpLoading} />
            </Box>
        )
    }

    // Search no-results CMS page: Optimizely has authored a page with SimpleAddress
    // 'search-no-results' — render it as a full page replacement with no PLP chrome.
    // Only applies to a pure keyword search with zero results; when the user has
    // active filters that produced zero hits we fall through so the filter/refinement
    // UI remains visible (same behaviour as before this feature was added).
    if (showNoResults && isSearch && !hasSelectedFilters && hasSearchNoResultsPage) {
        return (
            <Box data-testid="sf-product-list-page" data-search-no-results="true" {...rest}>
                <GDIDynamicContentProvider value={{searchTerm: searchQuery}}>
                    <Optimizely content={searchNoResultsPage} isLoading={isSearchNoResultsLoading} />
                </GDIDynamicContentProvider>
            </Box>
        )
    }

    /**************** Selected Filters ****************/
    const renderDesktopSelectedFilters = () => {
        if (!hasSelectedFilters) return null
        return (
            <HideOnMobile>
                <Box {...pageFiltersStyles.srPlpContainer}>
                    <SelectedRefinements
                        filters={productSearchResult?.refinements?.filter((r) =>
                            r?.values?.some((v) => v.hitCount > 0)
                        )}
                        toggleFilter={toggleFilter}
                        handleReset={resetFilters}
                        selectedFilterValues={productSearchResult?.selectedRefinements}
                        urlRefine={searchParams?.refine}
                    />
                </Box>
            </HideOnMobile>
        )
    }

    const renderMobileSelectedFilters = () => {
        if (!hasSelectedFilters) return null
        return (
            <HideOnDesktop>
                <Box {...pageFiltersStyles.srPlpContainer}>
                    <SelectedRefinements
                        filters={productSearchResult?.refinements}
                        toggleFilter={toggleFilter}
                        handleReset={resetFilters}
                        selectedFilterValues={productSearchResult?.selectedRefinements}
                        urlRefine={searchParams?.refine}
                    />
                </Box>
            </HideOnDesktop>
        )
    }

    /**************** SEO meta tags ****************/
    // Prefer live client query data (productSearchResult / category); fall back to the
    // server-injected seoMeta so the tags are present in SSR view-source. On the first
    // client render the queries are still resolving, so the fallback also keeps SSR and
    // hydration output identical.
    const seoMetaTags = productSearchResult?.pageMetaTags ?? seoMeta?.pageMetaTags ?? []
    const seoTitle =
        pageTitle ||
        seoMetaTags.find((tag) => tag.id === 'title')?.value ||
        category?.pageTitle ||
        seoMeta?.pageTitle ||
        searchQuery
    const seoCanonicalUrl = category?.c_canonicalURL ?? seoMeta?.canonicalUrl
    const seoAlternateURLs = category?.c_alternateURLs ?? seoMeta?.alternateURLs
    const seoDescriptionFallback =
        category?.pageDescription ?? seoMeta?.pageDescription ?? searchQuery
    const seoKeywordsFallback = category?.pageKeywords ?? seoMeta?.pageKeywords
    let hasDescriptionMetaTag = false
    let hasKeywordsTag = false
    return (
        <Box
            className="sf-product-list-page"
            data-testid="sf-product-list-page"
            layerStyle="PLPpage"
            {...rest}
        >
            <Seo title={seoTitle} noIndex={isSearch} canonicalUrl={seoCanonicalUrl}>
                {seoMetaTags.map((tag) => {
                    if (tag.id !== 'title') {
                        // Search results pages don't render a description meta tag.
                        if (isSearch && tag.id === 'description') return null
                        if (tag.id === 'description') hasDescriptionMetaTag = true
                        if (tag.id === 'keywords') hasKeywordsTag = true
                        // The SFCC `og:site_name`/`og:image` rules key off OriginalRequestURL,
                        // which is the SCAPI gateway URL in headless (never the storefront host)
                        // -> the brand is never detected and the rule always resolves the ELSE
                        // branch. Keep the BM-configured value but rewrite its brand slug to the
                        // brand of the page (from the URL path segment).
                        let content = tag.value
                        if (tag.id === 'og:site_name' && seoBrandId) {
                            content = seoBrandId.charAt(0).toUpperCase() + seoBrandId.slice(1)
                        } else if (tag.id === 'og:image' && seoBrandId) {
                            const otherBrand = seoBrandId === 'garage' ? 'dynamite' : 'garage'
                            content = tag.value.replace(new RegExp(otherBrand, 'g'), seoBrandId)
                        }
                        return (
                            <meta
                                key={tag.id}
                                {...(tag.id.startsWith('og:')
                                    ? {property: tag.id, content}
                                    : {name: tag.id, content})}
                            />
                        )
                    }
                })}
                {!hasDescriptionMetaTag && !isSearch && (
                    <meta name="description" content={seoDescriptionFallback} />
                )}
                {!hasKeywordsTag && <meta name="keywords" content={seoKeywordsFallback} />}
                {seoCanonicalUrl && <link rel="canonical" href={seoCanonicalUrl} />}
                {generateAlternateUrls(seoAlternateURLs)}
            </Seo>

            {!showNoResults && (
                <>
                    <AbovePageHeader />
                    <PageHeader
                        searchQuery={searchQuery}
                        category={category}
                        productSearchResult={productSearchResult}
                    />
                    {aboveProductListBlocks?.length > 0 && (
                        <Box my={{base: 6, lg: 8}}>
                            {aboveProductListBlocks.map((block, index) => (
                                <Box key={`above-plp-${block?.ContentLink?.GuidValue ?? index}`}>
                                    {renderLayoutsSwitch(block)}
                                </Box>
                            ))}
                        </Box>
                    )}
                </>
            )}

            {showNoResults && (
                <EmptySearchResultsTitle
                    hasSelectedFilters={
                        productSearchResult?.selectedRefinements
                            ? Object.keys(productSearchResult.selectedRefinements).filter(
                                  (key) => key !== 'q' && key !== 'cgid'
                              ).length > 0
                            : false
                    }
                />
            )}

            {(!showNoResults || hasSelectedFilters) && (
                <PageFilters
                    category={category}
                    productSearchResult={productSearchResult}
                    isLoading={isLoading}
                    toggleFilter={toggleFilter}
                    searchParams={searchParams}
                    resetFilters={resetFilters}
                    onSortClick={() => setSortOpen(true)}
                    onFiltersClick={() => onOpen()}
                    isVisible
                    gridView={gridView}
                    setGridView={setGridViewPersisted}
                    hideFiltersAndSort={hideFiltersAndSort}
                    hideSortAndGrid={showNoResults && hasSelectedFilters}
                />
            )}

            {!hideFiltersAndSort && showNoResults && hasSelectedFilters && (
                <Box>
                    {renderDesktopSelectedFilters()}
                    {renderMobileSelectedFilters()}
                </Box>
            )}

            {showNoResults ? (
                <>
                    <EmptySearchResults />
                    {lowResultsContent?.length > 0 && (
                        <Box data-testid="low-results-content">
                            {lowResultsContent.map((block, index) => (
                                <Box key={block?.ContentLink?.GuidValue ?? index}>
                                    {renderLayoutsSwitch(block)}
                                </Box>
                            ))}
                        </Box>
                    )}
                </>
            ) : (
                <>
                    {/* Visible when earlier pages exist before the current group (group start
                        > 1). A page-2 landing (group start 1) stays hidden; a page-4 landing
                        (group start 3) shows it even while page 3 loads. loadPrevious does a
                        scroll-preserving prepend, so the content stays put and the button
                        scrolls away as pages load above. See showLoadPrevious in the hook. */}
                    {showLoadPrevious && (
                        <Flex justifyContent="center" gap={4} my={7}>
                            <Button
                                variant="webPrimary"
                                onClick={loadPrevious}
                                width="100%"
                                maxWidth={{base: '100%', lg: '342px'}}
                            >
                                {formatMessage({
                                    id: 'plp.button.show.previous',
                                    defaultMessage: 'Show Previous'
                                })}
                            </Button>
                        </Flex>
                    )}

                    {!hideFiltersAndSort && renderDesktopSelectedFilters()}
                    {!hideFiltersAndSort && renderMobileSelectedFilters()}

                    <Grid templateColumns={{base: '1fr', md: '1fr'}} columnGap={6}>
                        <ProductGrid
                            allProducts={allProducts}
                            productSearchResult={productSearchResult}
                            skeletonPlaceholders={skeletonPlaceholders}
                            isRefetching={isRefetching}
                            isFetched={isFetched}
                            isCategoryChanging={isCategoryChanging}
                            searchParams={searchParams}
                            isAlternativeGridView={isAlternativeGridView}
                            gridColumns={gridColumns}
                            currentBreakpoint={currentBreakpoint}
                            isDesktopBreakpoint={isDesktopBreakpoint}
                            desktopGroupSize={desktopGroupSize}
                            gridInterruptorPositions={gridInterruptorPositions}
                            enrichProduct={enrichProduct}
                            observeProduct={observeProduct}
                            productRefs={productRefs}
                            prependScrollAnchor={prependScrollAnchor}
                            wishlist={wishlist}
                            addItemToWishlist={addItemToWishlist}
                            removeItemFromWishlist={removeItemFromWishlist}
                            handleProductClick={handleProductClick}
                            total={total}
                            page={page}
                            minPageLoaded={minPageLoaded}
                            maxPageLoaded={maxPageLoaded}
                            goToPage={goToPage}
                            isLoadingNewPage={isLoadingNewPage}
                            isSearch={isSearch}
                            getLimitForPage={getLimitForPage}
                            loadedLimitTotal={loadedLimitTotal}
                        />

                        {/* Hidden page-size select - kept for potential future use */}
                        <Flex
                            display="none"
                            justifyContent={['center', 'center', 'flex-start']}
                            paddingTop={8}
                        >
                            <Select
                                value={basePath}
                                onChange={({target}) => history.push(target.value)}
                            >
                                {limitUrls.map((href, index) => (
                                    <option key={href} value={href}>
                                        {DEFAULT_LIMIT_VALUES[index]}
                                    </option>
                                ))}
                            </Select>
                        </Flex>
                    </Grid>

                    {!isLoading && total !== undefined && total <= 4 && (
                        lowResultsContent?.length > 0 ? (
                            <Box data-testid="low-results-content">
                                {lowResultsContent.map((block, index) => (
                                    <Box key={block?.ContentLink?.GuidValue ?? index}>
                                        {renderLayoutsSwitch(block)}
                                    </Box>
                                ))}
                            </Box>
                        ) : (
                            <LowResultsCarousel />
                        )
                    )}

                    {belowProductListBlocks?.length > 0 && (
                        <Box marginTop={{base: 8, lg: 12}}>
                            {belowProductListBlocks.map((block, index) => (
                                <Box key={`below-plp-${block?.ContentLink?.GuidValue ?? index}`}>
                                    {renderLayoutsSwitch(block)}
                                </Box>
                            ))}
                        </Box>
                    )}
                </>
            )}
        </Box>
    )
}

ProductList.getTemplateName = () => 'product-list'

ProductList.propTypes = {
    onAddToWishlistClick: PropTypes.func,
    onRemoveWishlistClick: PropTypes.func,
    category: PropTypes.object
}

// PWA Kit getProps method to pass server-side data to the component

export default ProductList
